import React, { useContext, useEffect, useState } from "react";
import {
  Login,
  Documents,
  Register,
  Profile,
  Users,
  ResetPassword,
  Contact,
  NewPassword,
} from "./views";
import { BrowserRouter, Route, Switch, Redirect } from "react-router-dom";
import {
  AuthenticationProvider,
  AuthenticationContext,
} from "./providers/AuthenticationProvider";
import { ThemeProvider } from "styled-components";
import { Box } from "@rebass/grid";
import { Navbar } from "./components";
import { DocumentsProvider } from "./providers/DocumentsProvider";
import { GeoProvider } from "./providers/GeoProvider";
import { ProfileProvider } from "./providers/ProfileProvider";
import { UsersProvider } from "./providers/UsersProvider";
import colors from "./theme/Colors";
import { useTranslation } from "react-i18next";
import CookieConsent from "react-cookie-consent";
import { PrivacyPolicy } from "./views/privacy-policy";
import { Imprint } from "./views/imprint";
import { withTracker } from "./analytics/withTracker";

export const PrivateRoute = ({ component, ...rest }: any) => {
  const {
    checkAuthentication,
    authenticated,
    isAdmin,
    isAutoLogin,
  } = useContext(AuthenticationContext);

  useEffect(() => {
    checkAuthentication();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <Route
      {...rest}
      render={(props) => {
        if (!authenticated) return <Redirect to="/" />;
        if (isAutoLogin && props.match.path === "/profile")
          return <Redirect to="/documents" />;
        if (!isAdmin && props.match.path === "/users")
          return <Redirect to="/documents" />;
        return React.createElement(component, props);
      }}
    />
  );
};

function App() {
  const [brand, setBrand] = useState("brenntag");
  const { t } = useTranslation();

  useEffect(() => {
    if (process.env.REACT_APP_BRAND === "bcd") {
      setBrand("bcd");
    } else {
      setBrand("brenntag");
    }
  }, []);

  const getLogo = () => {
    return brand === "bcd" ? "bcd_logo.jpg" : "brenntag_logo.png";
  };

  return (
    <ThemeProvider theme={{ brand: brand }}>
      <BrowserRouter basename={process.env.PUBLIC_URL}>
        <AuthenticationProvider>
          <ProfileProvider>
            <UsersProvider>
              <Navbar
                logo={getLogo()}
                items={[
                  {
                    id: "documents",
                    title: t("DOCUMENTS"),
                    link: "/documents",
                    isAction: false,
                    protected: true,
                  },
                  {
                    id: "users",
                    title: t("USERS"),
                    link: "/users",
                    isAction: false,
                    protected: true,
                  },
                  {
                    id: "profile",
                    title: t("PROFILE"),
                    link: "/profile",
                    isAction: false,
                    protected: true,
                  },
                  {
                    id: "contact",
                    title: t("CONTACT"),
                    link: "/contact",
                    isAction: false,
                    protected: false,
                  },
                  {
                    id: "privacy-policy",
                    title: t("PRIVACY_POLICY"),
                    link: "/privacy-policy",
                    isAction: false,
                    protected: false,
                  },
                  {
                    id: "imprint",
                    title: t("IMPRINT"),
                    link: "/imprint",
                    isAction: false,
                    protected: false,
                  },
                  {
                    id: "logout",
                    title: t("LOG_OUT"),
                    isAction: true,
                    protected: true,
                  },
                ]}
              />

              <CookieConsent
                buttonText={t("COOKIE_CONSENT_BUTTON_TEXT")}
                extraCookieOptions={{ secure: true }}
              >
                {t("COOKIE_CONSENT_BODY_TEXT")}
              </CookieConsent>

              <Box
                id="page"
                pt={40}
                bg={
                  brand === "brenntag"
                    ? colors.brenntag.backgroundColor
                    : colors.bcd.backgroundColor
                }
                style={{ minHeight: "100vh" }}
              >
                <DocumentsProvider>
                  <GeoProvider>
                    <Switch>
                      <Route exact path="/" component={withTracker(Login)} />
                      <Route
                        exact
                        path="/Login.do"
                        component={withTracker(Login)}
                      />
                      <PrivateRoute
                        exact
                        path="/documents"
                        component={withTracker(Documents)}
                      />
                      <PrivateRoute
                        exact
                        path="/profile"
                        component={withTracker(Profile)}
                      />
                      <PrivateRoute
                        exact
                        path="/users"
                        component={withTracker(Users)}
                      />
                      <Route
                        exact
                        path="/register"
                        component={withTracker(Register)}
                      />
                      <Route
                        exact
                        path="/reset_password"
                        component={withTracker(ResetPassword)}
                      />
                      <Route
                        exact
                        path="/new_password"
                        component={withTracker(NewPassword)}
                      />
                      <Route
                        exact
                        path="/contact"
                        component={withTracker(Contact)}
                      />
                      <Route
                        exact
                        path="/privacy-policy"
                        component={withTracker(PrivacyPolicy)}
                      />
                      <Route
                        exact
                        path="/imprint"
                        component={withTracker(Imprint)}
                      />
                      <Redirect to="/" />
                    </Switch>
                  </GeoProvider>
                </DocumentsProvider>
              </Box>
            </UsersProvider>
          </ProfileProvider>
        </AuthenticationProvider>
      </BrowserRouter>
    </ThemeProvider>
  );
}

export default App;
